import React, { useEffect, useRef, useState, useCallback } from 'react'
import { MapPinIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline'

interface LocationResult {
  latitude: number
  longitude: number
  address: string
  placeId?: string
}

interface GooglePlacesLocationPickerProps {
  value?: { latitude: string; longitude: string }
  onChange: (location: LocationResult) => void
  error?: string
  label?: string
  placeholder?: string
  required?: boolean
}

interface Prediction {
  place_id: string
  description: string
  structured_formatting: {
    main_text: string
    secondary_text: string
  }
}

const GOOGLE_MAPS_API_KEY = (import.meta as any).env.VITE_GOOGLE_MAPS_API_KEY || ''

let googleScriptLoaded = false
let googleScriptLoading = false
const loadCallbacks: (() => void)[] = []

function loadGoogleMapsScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (googleScriptLoaded && window.google?.maps?.places) {
      resolve()
      return
    }

    if (googleScriptLoading) {
      loadCallbacks.push(resolve)
      return
    }

    if (!GOOGLE_MAPS_API_KEY) {
      reject(new Error('Google Maps API key is not configured'))
      return
    }

    googleScriptLoading = true
    const script = document.createElement('script')
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`
    script.async = true
    script.defer = true
    script.onload = () => {
      googleScriptLoaded = true
      googleScriptLoading = false
      resolve()
      loadCallbacks.forEach(cb => cb())
      loadCallbacks.length = 0
    }
    script.onerror = () => {
      googleScriptLoading = false
      reject(new Error('Failed to load Google Maps script'))
    }
    document.head.appendChild(script)
  })
}

const GooglePlacesLocationPicker: React.FC<GooglePlacesLocationPickerProps> = ({
  value,
  onChange,
  error,
  label = 'Institution Location *',
  placeholder = 'Search for institution address...',
  required = true
}) => {
  const [query, setQuery] = useState('')
  const [predictions, setPredictions] = useState<Prediction[]>([])
  const [selectedAddress, setSelectedAddress] = useState('')
  const [showDropdown, setShowDropdown] = useState(false)
  const [loading, setLoading] = useState(false)
  const [scriptReady, setScriptReady] = useState(false)
  const [scriptError, setScriptError] = useState('')

  const autocompleteServiceRef = useRef<google.maps.places.AutocompleteService | null>(null)
  const geocoderRef = useRef<google.maps.Geocoder | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  // Load Google Maps script
  useEffect(() => {
    loadGoogleMapsScript()
      .then(() => {
        autocompleteServiceRef.current = new google.maps.places.AutocompleteService()
        geocoderRef.current = new google.maps.Geocoder()
        setScriptReady(true)
      })
      .catch((err) => {
        setScriptError(err.message)
      })
  }, [])

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const searchPlaces = useCallback((input: string) => {
    if (!autocompleteServiceRef.current || input.length < 3) {
      setPredictions([])
      return
    }

    setLoading(true)
    autocompleteServiceRef.current.getPlacePredictions(
      {
        input,
        types: ['establishment', 'geocode']
      },
      (results, status) => {
        setLoading(false)
        if (status === google.maps.places.PlacesServiceStatus.OK && results) {
          setPredictions(results as unknown as Prediction[])
          setShowDropdown(true)
        } else {
          setPredictions([])
        }
      }
    )
  }, [])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value
    setQuery(val)

    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => searchPlaces(val), 300)
  }

  const handleSelectPrediction = (prediction: Prediction) => {
    if (!geocoderRef.current) return

    setLoading(true)
    geocoderRef.current.geocode({ placeId: prediction.place_id }, (results, status) => {
      setLoading(false)
      if (status === google.maps.GeocoderStatus.OK && results && results[0]) {
        const location = results[0].geometry.location
        const lat = location.lat()
        const lng = location.lng()

        setSelectedAddress(prediction.description)
        setQuery('')
        setShowDropdown(false)
        setPredictions([])

        onChange({
          latitude: lat,
          longitude: lng,
          address: prediction.description,
          placeId: prediction.place_id
        })
      }
    })
  }

  const handleClear = () => {
    setSelectedAddress('')
    setQuery('')
    setPredictions([])
    onChange({ latitude: 0, longitude: 0, address: '' })
  }

  const hasLocation = value?.latitude && value?.longitude &&
    parseFloat(value.latitude) !== 0 && parseFloat(value.longitude) !== 0

  if (scriptError) {
    return (
      <div className="md:col-span-2">
        <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
        <p className="text-sm text-red-500">
          Google Maps failed to load. Please check your API key configuration.
        </p>
      </div>
    )
  }

  return (
    <div className="md:col-span-2" ref={containerRef}>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
      </label>

      {/* Selected location display */}
      {hasLocation && selectedAddress ? (
        <div className="flex items-start gap-3 p-3 bg-green-50 border border-green-200 rounded-lg mb-2">
          <MapPinIcon className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-green-900 truncate">{selectedAddress}</p>
            <p className="text-xs text-green-700 mt-0.5">
              {parseFloat(value!.latitude).toFixed(6)}, {parseFloat(value!.longitude).toFixed(6)}
            </p>
          </div>
          <button
            type="button"
            onClick={handleClear}
            className="text-green-600 hover:text-green-800 flex-shrink-0"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      ) : null}

      {/* Search input */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
        </div>
        <input
          type="text"
          value={query}
          onChange={handleInputChange}
          onFocus={() => predictions.length > 0 && setShowDropdown(true)}
          placeholder={hasLocation ? 'Search to change location...' : placeholder}
          disabled={!scriptReady}
          className={`block w-full pl-9 pr-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm ${
            error ? 'border-red-300' : 'border-gray-300'
          } ${!scriptReady ? 'bg-gray-50 cursor-wait' : ''}`}
        />
        {loading && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
            <div className="h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </div>

      {/* Autocomplete dropdown */}
      {showDropdown && predictions.length > 0 && (
        <div className="absolute z-50 mt-1 w-full max-w-lg bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
          {predictions.map((prediction) => (
            <button
              key={prediction.place_id}
              type="button"
              onClick={() => handleSelectPrediction(prediction)}
              className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-0 transition-colors"
            >
              <div className="flex items-start gap-2">
                <MapPinIcon className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {prediction.structured_formatting.main_text}
                  </p>
                  <p className="text-xs text-gray-500">
                    {prediction.structured_formatting.secondary_text}
                  </p>
                </div>
              </div>
            </button>
          ))}
          <div className="px-4 py-1.5 bg-gray-50 text-right">
            <span className="text-[10px] text-gray-400">Powered by Google</span>
          </div>
        </div>
      )}

      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}

      {!hasLocation && !error && (
        <p className="mt-1 text-xs text-gray-500">
          Search for your institution to set its GPS coordinates
        </p>
      )}
    </div>
  )
}

export default GooglePlacesLocationPicker
